"use client";

import { useState } from "react";
import { SchemaField } from "@papier/grapesjs-editor/src/components/editor/schema-builder/types/schema";
import { createDefaultField, findFieldById } from "@/utils/schema-utils";

export const useSchema = () => {
  const [schema, setSchema] = useState<SchemaField[]>([]);

  const addFieldToParent = (
    fields: SchemaField[],
    parentId: string,
    newField: SchemaField,
  ): SchemaField[] => {
    const addFieldRecursive = (
      fields: SchemaField[],
    ): { fields: SchemaField[]; found: boolean } => {
      for (let i = 0; i < fields.length; i++) {
        const field = fields[i];

        // Direct match - this field is the target parent
        if (field.id === parentId && field.type === "object") {
          const updatedField = {
            ...field,
            objectFields: [
              ...(field.objectFields || []),
              { ...newField, parentId, parentType: "object" as const },
            ],
          };
          return {
            fields: [...fields.slice(0, i), updatedField, ...fields.slice(i + 1)],
            found: true,
          };
        }

        // Check array item type
        if (field.arrayItemType?.id === parentId && field.arrayItemType.type === "object") {
          const updatedField = {
            ...field,
            arrayItemType: {
              ...field.arrayItemType,
              objectFields: [
                ...(field.arrayItemType.objectFields || []),
                { ...newField, parentId, parentType: "object" as const },
              ],
            },
          };
          return {
            fields: [...fields.slice(0, i), updatedField, ...fields.slice(i + 1)],
            found: true,
          };
        }

        // Recurse into object fields
        if (field.objectFields && field.type === "object") {
          const result = addFieldRecursive(field.objectFields);
          if (result.found) {
            const updatedField = {
              ...field,
              objectFields: result.fields,
            };
            return {
              fields: [...fields.slice(0, i), updatedField, ...fields.slice(i + 1)],
              found: true,
            };
          }
        }

        // Recurse into array item object fields
        if (field.arrayItemType?.type === "object" && field.arrayItemType.objectFields) {
          const result = addFieldRecursive(field.arrayItemType.objectFields);
          if (result.found) {
            const updatedField = {
              ...field,
              arrayItemType: {
                ...field.arrayItemType,
                objectFields: result.fields,
              },
            };
            return {
              fields: [...fields.slice(0, i), updatedField, ...fields.slice(i + 1)],
              found: true,
            };
          }
        }
      }

      return { fields, found: false };
    };

    const result = addFieldRecursive(fields);
    return result.fields;
  };

  const addField = (type: SchemaField["type"], parentId?: string) => {
    const newField = createDefaultField(type);

    if (parentId) {
      setSchema((prev) => addFieldToParent(prev, parentId, newField));
    } else {
      setSchema((prev) => [...prev, newField]);
    }
  };

  const updateField = (id: string, updates: Partial<SchemaField>) => {
    const updateInFields = (fields: SchemaField[]): SchemaField[] => {
      return fields.map((field) => {
        if (field.id === id) {
          return { ...field, ...updates };
        }
        if (field.objectFields) {
          return {
            ...field,
            objectFields: updateInFields(field.objectFields),
          };
        }
        if (field.arrayItemType?.id === id) {
          return {
            ...field,
            arrayItemType: { ...field.arrayItemType, ...updates },
          };
        }
        return field;
      });
    };

    setSchema((prev) => updateInFields(prev));
  };

  const deleteField = (id: string) => {
    const deleteFromFields = (fields: SchemaField[]): SchemaField[] => {
      return fields
        .filter((field) => field.id !== id)
        .map((field) => ({
          ...field,
          objectFields: field.objectFields ? deleteFromFields(field.objectFields) : undefined,
        }));
    };

    setSchema((prev) => deleteFromFields(prev));
  };

  const moveField = (
    draggedId: string,
    targetId: string,
    position: "before" | "after" | "inside",
  ) => {
    const draggedField = findFieldById(schema, draggedId);
    if (!draggedField) return;

    setSchema((prev) => {
      const removeFromFields = (fields: SchemaField[]): SchemaField[] => {
        return fields
          .filter((field) => field.id !== draggedId)
          .map((field) => ({
            ...field,
            objectFields: field.objectFields ? removeFromFields(field.objectFields) : undefined,
          }));
      };

      let newSchema = removeFromFields(prev);

      if (position === "inside") {
        newSchema = addFieldToParent(newSchema, targetId, { ...draggedField, parentId: targetId });
      } else {
        const insertAtPosition = (fields: SchemaField[]): SchemaField[] => {
          const targetIndex = fields.findIndex((f) => f.id === targetId);
          if (targetIndex !== -1) {
            const insertIndex = position === "before" ? targetIndex : targetIndex + 1;
            const newFields = [...fields];
            newFields.splice(insertIndex, 0, { ...draggedField, parentId: undefined });
            return newFields;
          }
          return fields.map((field) => ({
            ...field,
            objectFields: field.objectFields ? insertAtPosition(field.objectFields) : undefined,
          }));
        };
        newSchema = insertAtPosition(newSchema);
      }

      return newSchema;
    });
  };

  const updateArrayItemType = (arrayId: string, newType: SchemaField["type"]) => {
    const newItemType = createDefaultField(newType, "item");
    updateField(arrayId, { arrayItemType: newItemType });

    const arrayField = findFieldById(schema, arrayId);
    if (arrayField && newItemType.examples) {
      const newArrayExamples = [newItemType.examples.slice(0, 2)];
      updateField(arrayId, { examples: newArrayExamples });
    }
  };

  return {
    schema,
    setSchema,
    addField,
    updateField,
    deleteField,
    moveField,
    updateArrayItemType,
    addFieldToParent,
  };
};
